import React, { useCallback } from 'react'

import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'

import { RecipeQueryResponse } from '@library/api-menu-experience'
import { MenuRecipe } from '@library/menu-service/queries/menuApiMenuRecipe/types'

import { useCurrentCollectionId, useCollectionRecipeSets } from 'hooks/menu-collections'
import { useBaseRecipeId } from 'hooks/menu-recipes/useBaseRecipeId'
import { RecipeCoreId, RecipeUUID } from 'reducers/menu-recipes/recipes'
import { useFetchRecipe } from 'routes/Menu/components/Detail/hooks/useFetchRecipe'
import { Detail as SpicyDetail } from 'routes/Menu/components/SpicyDetail/Detail'
import { useSpicyRecipeDetailsEnabled } from 'routes/Menu/hooks/useSpicyRecipeDetailsEnabled'
import { getBasketDate, getBasketMenuUuid, getNumPortions } from 'selectors/basket'

import { closeRecipeDetails } from '../../../actions/closeRecipeDetails'
import {
  getMenuRecipeForDetails,
  getMenuRecipeUuidForDetails,
} from '../../../selectors/menuRecipeDetails'
import { Detail } from '../Detail'

interface DetailOverlayProps {
  showOverlay?: boolean
}

/**
 * Returns the position of the recipe in the collection recipe sets list,
 * works for cases when `recipeId` is an option in the recipe set
 *
 * If recipe is not in the collection, returns undefined
 */
const useRecipePosition = (recipeId: RecipeUUID | null): number | undefined => {
  const currentCollectionId = useCurrentCollectionId()
  const currentCollectionRecipeSets = useCollectionRecipeSets(currentCollectionId)

  if (!recipeId) {
    return undefined
  }

  const recipeIndex = currentCollectionRecipeSets.findIndex(({ recipeIds }) =>
    recipeIds.has(recipeId),
  )

  return recipeIndex >= 0 ? recipeIndex + 1 : undefined
}

const DetailOverlay = ({ showOverlay: showOverlayProp }: DetailOverlayProps) => {
  const spicyRecipeDetailsEnabled = useSpicyRecipeDetailsEnabled()
  const showOverlay = Boolean(useSelector(getMenuRecipeUuidForDetails)) && showOverlayProp
  const recipeUuid = useSelector(getMenuRecipeUuidForDetails)
  const menuRecipeDetailShow = recipeUuid || ''
  const recipePosition = useRecipePosition(recipeUuid as RecipeUUID | null)
  const baseRecipeId = useBaseRecipeId(recipeUuid as RecipeUUID | null)

  const numPortions = useSelector(getNumPortions)
  const detailRecipe = useSelector(getMenuRecipeForDetails)
  const dispatch = useDispatch()
  const onClose = useCallback(() => dispatch(closeRecipeDetails()), [dispatch])
  const deliveryDate = useSelector(getBasketDate)
  const menuId = useSelector(getBasketMenuUuid)
  const collectionId = useCurrentCollectionId()
  const shouldFetchOptions = true

  const { loading, recipe, error, reload } = useFetchRecipe(
    menuId,
    recipeUuid,
    numPortions,
    deliveryDate,
    shouldFetchOptions,
    collectionId,
    spicyRecipeDetailsEnabled,
  )

  if (!(showOverlay && detailRecipe && menuRecipeDetailShow)) {
    return null
  }

  return spicyRecipeDetailsEnabled ? (
    <SpicyDetail
      recipe={recipe as RecipeQueryResponse}
      recipeCoreId={detailRecipe.coreId}
      showOverlay={showOverlay}
      loading={loading}
      error={error}
      position={recipePosition?.toString()}
      onClose={onClose}
      reload={reload}
    />
  ) : (
    <Detail
      uuid={detailRecipe.id}
      coreId={detailRecipe.coreId}
      title={detailRecipe.name}
      showOverlay={showOverlay}
      loading={loading}
      error={error}
      recipe={recipe as MenuRecipe}
      reload={reload}
      position={recipePosition?.toString()}
      onClose={onClose}
      baseRecipeId={baseRecipeId as RecipeCoreId}
    />
  )
}

DetailOverlay.propTypes = {
  showOverlay: PropTypes.bool,
}

DetailOverlay.defaultProps = {
  showOverlay: false,
}

export { DetailOverlay }
